import React, { useState } from 'react';
import { MainLayout } from '../layouts/MainLayout';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Activity, AlertTriangle, CheckCircle, XCircle, Server, Wifi, Database, HardDrive, Cpu, Network, ArrowLeft } from 'lucide-react';
interface MonitoringProps {
  onLogout: () => void;
}
interface ClientStatus {
  id: string;
  name: string;
  city: string;
  status: 'healthy' | 'warning' | 'critical';
  coordinates: {
    x: number;
    y: number;
  };
  devices: number;
  uptime: number;
  lastCheck: string;
}
const mockClients: ClientStatus[] = [{
  id: '1',
  name: 'Zahnarztpraxis Dr. Weber',
  city: 'Hamburg',
  status: 'healthy',
  coordinates: {
    x: 48,
    y: 18
  },
  devices: 45,
  uptime: 99.8,
  lastCheck: 'vor 2 Min'
}, {
  id: '2',
  name: 'Kanzlei Schmidt & Partner',
  city: 'Berlin',
  status: 'warning',
  coordinates: {
    x: 62,
    y: 28
  },
  devices: 32,
  uptime: 97.2,
  lastCheck: 'vor 1 Min'
}, {
  id: '3',
  name: 'Zahnarztpraxis Dr. Hoffmann',
  city: 'München',
  status: 'healthy',
  coordinates: {
    x: 55,
    y: 68
  },
  devices: 18,
  uptime: 99.9,
  lastCheck: 'vor 3 Min'
}, {
  id: '4',
  name: 'Rechtsanwälte Becker & Koch',
  city: 'Frankfurt',
  status: 'critical',
  coordinates: {
    x: 45,
    y: 48
  },
  devices: 28,
  uptime: 89.5,
  lastCheck: 'vor 1 Min'
}, {
  id: '5',
  name: 'Kieferorthopädie Meyer',
  city: 'Köln',
  status: 'healthy',
  coordinates: {
    x: 32,
    y: 42
  },
  devices: 52,
  uptime: 99.5,
  lastCheck: 'vor 4 Min'
}];
export function Monitoring({
  onLogout
}: MonitoringProps) {
  const [selectedClient, setSelectedClient] = useState<ClientStatus | null>(null);
  const getStatusColor = (status: ClientStatus['status']) => {
    switch (status) {
      case 'healthy':
        return 'bg-emerald-500';
      case 'warning':
        return 'bg-amber-500';
      case 'critical':
        return 'bg-red-500';
    }
  };
  const getStatusBadge = (status: ClientStatus['status']) => {
    switch (status) {
      case 'healthy':
        return <Badge variant="success" data-id="element-841">Gesund</Badge>;
      case 'warning':
        return <Badge variant="warning" data-id="element-842">Warnung</Badge>;
      case 'critical':
        return <Badge variant="error" data-id="element-843">Kritisch</Badge>;
    }
  };
  const healthyCount = mockClients.filter(c => c.status === 'healthy').length;
  const warningCount = mockClients.filter(c => c.status === 'warning').length;
  const criticalCount = mockClients.filter(c => c.status === 'critical').length;
  if (selectedClient) {
    return <MainLayout title="Monitoring - Detailansicht" onLogout={onLogout} data-id="element-844">
        <Button variant="ghost" leftIcon={<ArrowLeft className="h-4 w-4" data-id="element-846" />} onClick={() => setSelectedClient(null)} className="mb-6" data-id="element-845">
          Zurück zur Übersicht
        </Button>

        {/* Client Header */}
        <Card className="mb-6" data-id="element-847">
          <div className="flex items-center justify-between" data-id="element-848">
            <div data-id="element-849">
              <h2 className="text-2xl font-bold text-slate-900 mb-1" data-id="element-850">
                {selectedClient.name}
              </h2>
              <p className="text-slate-600" data-id="element-851">{selectedClient.city}</p>
            </div>
            <div className="flex items-center gap-4" data-id="element-852">
              {getStatusBadge(selectedClient.status)}
              <div className="text-right" data-id="element-853">
                <p className="text-sm text-slate-600" data-id="element-854">Uptime</p>
                <p className="text-2xl font-bold text-slate-900" data-id="element-855">
                  {selectedClient.uptime}%
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Infrastructure Sunburst Visualization */}
        <Card title="IT Infrastructure" className="mb-6" data-id="element-856">
          <div className="relative w-full h-[600px] bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl overflow-hidden flex items-center justify-center" data-id="element-857">
            {/* Sunburst Chart - Using uploaded image */}
            <div className="relative w-full h-full flex items-center justify-center p-8" data-id="element-858">
              <img src="https://cdn.magicpatterns.com/uploads/sZVatygHGeEaSCXkLhUsYK/Screenshot_2025-12-07_164920.png" alt="IT Infrastructure Sunburst Diagram" className="max-w-full max-h-full object-contain" data-id="element-859" />
            </div>
          </div>
        </Card>

        {/* Device Categories */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-860">
          <Card data-id="element-861">
            <div className="flex items-center gap-3" data-id="element-862">
              <div className="p-3 bg-emerald-100 rounded-lg" data-id="element-863">
                <Network className="h-6 w-6 text-emerald-600" data-id="element-864" />
              </div>
              <div data-id="element-865">
                <p className="text-sm text-slate-600" data-id="element-866">Network</p>
                <p className="text-2xl font-bold text-slate-900" data-id="element-867">12</p>
                <p className="text-xs text-emerald-600 font-medium" data-id="element-868">
                  Alle online
                </p>
              </div>
            </div>
          </Card>

          <Card data-id="element-869">
            <div className="flex items-center gap-3" data-id="element-870">
              <div className="p-3 bg-red-100 rounded-lg" data-id="element-871">
                <Server className="h-6 w-6 text-red-600" data-id="element-872" />
              </div>
              <div data-id="element-873">
                <p className="text-sm text-slate-600" data-id="element-874">Server</p>
                <p className="text-2xl font-bold text-slate-900" data-id="element-875">8</p>
                <p className="text-xs text-red-600 font-medium" data-id="element-876">1 Warnung</p>
              </div>
            </div>
          </Card>

          <Card data-id="element-877">
            <div className="flex items-center gap-3" data-id="element-878">
              <div className="p-3 bg-amber-100 rounded-lg" data-id="element-879">
                <Database className="h-6 w-6 text-amber-600" data-id="element-880" />
              </div>
              <div data-id="element-881">
                <p className="text-sm text-slate-600" data-id="element-882">Storage</p>
                <p className="text-2xl font-bold text-slate-900" data-id="element-883">6</p>
                <p className="text-xs text-emerald-600 font-medium" data-id="element-884">
                  Alle online
                </p>
              </div>
            </div>
          </Card>

          <Card data-id="element-885">
            <div className="flex items-center gap-3" data-id="element-886">
              <div className="p-3 bg-blue-100 rounded-lg" data-id="element-887">
                <Cpu className="h-6 w-6 text-blue-600" data-id="element-888" />
              </div>
              <div data-id="element-889">
                <p className="text-sm text-slate-600" data-id="element-890">Workstations</p>
                <p className="text-2xl font-bold text-slate-900" data-id="element-891">19</p>
                <p className="text-xs text-emerald-600 font-medium" data-id="element-892">
                  Alle online
                </p>
              </div>
            </div>
          </Card>
        </div>
      </MainLayout>;
  }
  return <MainLayout title="System Monitoring" onLogout={onLogout} data-id="element-893">
      {/* Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6" data-id="element-894">
        <Card data-id="element-895">
          <div className="flex items-center gap-3" data-id="element-896">
            <div className="p-3 bg-emerald-100 rounded-lg" data-id="element-897">
              <CheckCircle className="h-6 w-6 text-emerald-600" data-id="element-898" />
            </div>
            <div data-id="element-899">
              <p className="text-sm text-slate-600" data-id="element-900">Gesunde Systeme</p>
              <p className="text-3xl font-bold text-slate-900" data-id="element-901">
                {healthyCount}
              </p>
            </div>
          </div>
        </Card>

        <Card data-id="element-902">
          <div className="flex items-center gap-3" data-id="element-903">
            <div className="p-3 bg-amber-100 rounded-lg" data-id="element-904">
              <AlertTriangle className="h-6 w-6 text-amber-600" data-id="element-905" />
            </div>
            <div data-id="element-906">
              <p className="text-sm text-slate-600" data-id="element-907">Warnungen</p>
              <p className="text-3xl font-bold text-slate-900" data-id="element-908">
                {warningCount}
              </p>
            </div>
          </div>
        </Card>

        <Card data-id="element-909">
          <div className="flex items-center gap-3" data-id="element-910">
            <div className="p-3 bg-red-100 rounded-lg" data-id="element-911">
              <XCircle className="h-6 w-6 text-red-600" data-id="element-912" />
            </div>
            <div data-id="element-913">
              <p className="text-sm text-slate-600" data-id="element-914">Kritische Probleme</p>
              <p className="text-3xl font-bold text-slate-900" data-id="element-915">
                {criticalCount}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Germany Map with Client Status */}
      <Card title="Kunden-Status Übersicht" data-id="element-916">
        <div className="relative w-full h-[600px] bg-slate-50 rounded-xl overflow-hidden" data-id="element-917">
          {/* Germany Map */}
          <img src="https://cdn.magicpatterns.com/uploads/dFu2jPnJgcDptsiZKEPRsq/pasted-image.jpg" alt="Deutschlandkarte" className="w-full h-full object-contain opacity-20" data-id="element-918" />

          {/* Client Pins */}
          <div className="absolute inset-0" data-id="element-919">
            {mockClients.map(client => <button key={client.id} onClick={() => setSelectedClient(client)} className="absolute group" style={{
            left: `${client.coordinates.x}%`,
            top: `${client.coordinates.y}%`,
            transform: 'translate(-50%, -50%)'
          }} data-id="element-920">
                {/* Pulse Animation */}
                <div className={`absolute inset-0 ${getStatusColor(client.status)} rounded-full opacity-75 animate-ping`} data-id="element-921"></div>

                {/* Pin */}
                <div className={`relative w-6 h-6 ${getStatusColor(client.status)} rounded-full border-4 border-white shadow-lg group-hover:scale-125 transition-transform cursor-pointer`} data-id="element-922"></div>

                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-923">
                  <div className="bg-slate-900 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-xl" data-id="element-924">
                    <p className="font-semibold" data-id="element-925">{client.name}</p>
                    <p className="text-slate-300" data-id="element-926">{client.city}</p>
                    <p className="text-slate-300" data-id="element-927">Uptime: {client.uptime}%</p>
                  </div>
                  <div className="w-2 h-2 bg-slate-900 absolute left-1/2 -translate-x-1/2 -bottom-1 rotate-45" data-id="element-928"></div>
                </div>
              </button>)}
          </div>
        </div>
      </Card>

      {/* Client List */}
      <Card title="Alle Kunden" className="mt-6" data-id="element-929">
        <div className="space-y-3" data-id="element-930">
          {mockClients.map(client => <button key={client.id} onClick={() => setSelectedClient(client)} className="w-full flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-teal-300 hover:bg-teal-50/50 transition-all text-left" data-id="element-931">
              <div className="flex items-center gap-4" data-id="element-932">
                <div className={`w-4 h-4 ${getStatusColor(client.status)} rounded-full`} data-id="element-933"></div>
                <div data-id="element-934">
                  <h3 className="font-semibold text-slate-900" data-id="element-935">
                    {client.name}
                  </h3>
                  <p className="text-sm text-slate-600" data-id="element-936">{client.city}</p>
                </div>
              </div>

              <div className="flex items-center gap-6" data-id="element-937">
                <div className="text-right" data-id="element-938">
                  <p className="text-sm text-slate-600" data-id="element-939">Geräte</p>
                  <p className="text-lg font-bold text-slate-900" data-id="element-940">
                    {client.devices}
                  </p>
                </div>
                <div className="text-right" data-id="element-941">
                  <p className="text-sm text-slate-600" data-id="element-942">Uptime</p>
                  <p className="text-lg font-bold text-slate-900" data-id="element-943">
                    {client.uptime}%
                  </p>
                </div>
                <div className="text-right" data-id="element-944">
                  <p className="text-sm text-slate-600" data-id="element-945">Letzter Check</p>
                  <p className="text-sm text-slate-900" data-id="element-946">{client.lastCheck}</p>
                </div>
                {getStatusBadge(client.status)}
              </div>
            </button>)}
        </div>
      </Card>
    </MainLayout>;
}